// SignUp.jsx - Create Account component for VibrantCast
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { Eye, EyeOff, AlertCircle, Check } from 'lucide-react';
import '../styles/Auth.css';

const SignUp = () => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        navigate('/curator/dashboard', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = [];
    
    if (password.length >= 8) score++;
    else feedback.push('At least 8 characters');
    
    if (/[A-Z]/.test(password)) score++;
    else feedback.push('Uppercase letter');
    
    if (/[a-z]/.test(password)) score++;
    else feedback.push('Lowercase letter');
    
    if (/[0-9]/.test(password)) score++;
    else feedback.push('Number');
    
    if (/[^A-Za-z0-9]/.test(password)) score++;
    else feedback.push('Special character');
    
    return {
      score,
      feedback: feedback.length > 0 ? `Add: ${feedback.join(', ')}` : 'Strong password!'
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  // Generate unique handle from email (matches Flutter logic)
  const generateUniqueHandle = async (email) => {
    const baseHandle = email.split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    let handle = baseHandle;
    let counter = 1;
    
    // Check if handle exists
    while (true) {
      const { data, error } = await supabase
        .from('users')
        .select('handle')
        .eq('handle', handle)
        .single();
      
      if (error && error.code === 'PGRST116') {
        // No matching row found, handle is available
        break;
      }
      
      // Handle exists, try with counter
      handle = `${baseHandle}-${counter}`;
      counter++;
      
      if (counter > 100) {
        // Fallback with random suffix
        handle = `${baseHandle}-${Date.now().toString(36)}`;
        break;
      }
    }
    
    return handle;
  };

  // Create user profile after signup (matches Flutter createUserProfileAfterSignup)
  const createUserProfileAfterSignup = async (userId, email, firstName, lastName) => {
    try {
      // Generate unique handle
      const handle = await generateUniqueHandle(email);
      
      // Create user record
      const { error: userError } = await supabase
        .from('users')
        .insert({
          uuid: userId,
          email: email,
          handle: handle,
          first_name: firstName,
          last_name: lastName
        });
      
      if (userError) {
        console.error('Error creating user profile:', userError);
        throw userError;
      }
      
      // Assign default role (role_id = 4 is Artist, matching Flutter)
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role_id: 4 // Artist role
        });
      
      if (roleError) {
        console.error('Error assigning user role:', roleError);
        // Don't throw - user was created, role assignment can be fixed later
      }
      
      console.log('User profile created successfully with handle:', handle);
      return { handle };
    } catch (error) {
      console.error('Error in createUserProfileAfterSignup:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const { email, firstName, lastName, password, confirmPassword } = formData;

    // Validation
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      // Create auth user with metadata
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            display_name: `${firstName} ${lastName}`.trim()
          },
          emailRedirectTo: `${window.location.origin}/curator/dashboard`
        }
      });

      if (signUpError) throw signUpError;

      if (data.user) {
        // Check if email confirmation is required
        if (data.user.identities?.length === 0) {
          setError('An account with this email already exists. Please try logging in.');
          setLoading(false);
          return;
        }

        // If user is confirmed (email confirmation disabled in Supabase)
        if (data.session) {
          // Create user profile immediately
          await createUserProfileAfterSignup(
            data.user.id,
            email,
            firstName,
            lastName
          );
          
          setMessage('Account created successfully! Redirecting...');
          setTimeout(() => {
            navigate('/curator/dashboard', { replace: true });
          }, 1500);
        } else {
          // Email confirmation required
          setMessage('Please check your email to confirm your account. You can close this page.');
        }
      }
    } catch (error) {
      console.error('SignUp error:', error);
      if (error.message.includes('already registered')) {
        setError('An account with this email already exists. Please try logging in.');
      } else {
        setError(error.message || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/curator/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google sign up error:', error);
      setError(error.message || 'Failed to sign up with Google');
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    const { score } = passwordStrength;
    if (score <= 2) return '#ef4444';
    if (score <= 3) return '#f59e0b';
    return '#22c55e';
  };

  const getPasswordStrengthWidth = () => {
    return `${(passwordStrength.score / 5) * 100}%`;
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo-container">
            <img 
              src="https://res.cloudinary.com/dh9jfogh1/image/upload/t_fitmax-300/v1729693346/vibrant-logo_ds6pwg.png" 
              alt="VibrantCast" 
              className="auth-logo-inline"
            />
          </div>
          <h2>Create Account</h2>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={20} />
            <div>
              <div className="error-title">Sign Up Failed</div>
              <p className="error-message">{error}</p>
            </div>
          </div>
        )}

        {message && (
          <div className="auth-success">
            <Check size={20} />
            <div>
              <div className="success-title">Success!</div>
              <p className="success-message">{message}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group form-group-half">
              <label htmlFor="firstName">First Name</label>
              <div className="input-container">
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group form-group-half">
              <label htmlFor="lastName">Last Name</label>
              <div className="input-container">
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  required
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-container">
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-container">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.password && (
              <div className="password-strength">
                <div className="password-strength-bar">
                  <div 
                    className="password-strength-fill"
                    style={{ 
                      width: getPasswordStrengthWidth(),
                      backgroundColor: getPasswordStrengthColor()
                    }}
                  />
                </div>
                <span 
                  className="password-strength-text"
                  style={{ color: getPasswordStrengthColor() }}
                >
                  {passwordStrength.feedback}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-container">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <span className="password-mismatch">Passwords do not match</span>
            )}
            {formData.confirmPassword && formData.password === formData.confirmPassword && formData.confirmPassword.length > 0 && (
              <span className="password-match">
                <Check size={14} /> Passwords match
              </span>
            )}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || (formData.password !== formData.confirmPassword)}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          onClick={handleGoogleSignUp}
          className="google-signin-btn"
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="auth-footer">
          <div className="footer-links">
            <div className="signup-prompt">
              Already have an account?{' '}
              <Link to="/login" className="footer-link">
                Sign in
              </Link>
            </div>
          </div>
          
          <div className="legal-text">
            <p>By creating an account, you agree to our</p>
            <div className="legal-links">
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <span>and</span>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;